<template>
    <Teleport to="body">
        <transition name="fade">
            <div
                v-if="visible && !skipOpen && !backendOnboarded"
                :class="containerPositionClasses"
                aria-live="polite"
            >
                <!-- Mobile overlay to block interactions behind the teaser -->
                <div
                    class="absolute inset-0 bg-[#1E293B] opacity-50 sm:hidden z-40 pointer-events-auto"
                    aria-hidden="true"
                ></div>
                <div
                    class="absolute left-0 right-0 bottom-0 sm:static sm:right-auto sm:left-auto sm:bottom-auto z-50 sm:z-auto pointer-events-auto flex w-full sm:w-[480px] sm:max-w-[480px] shadow-none sm:shadow-lg dark:shadow-slate-900/50 flex-col items-start rounded-t-3xl sm:rounded-lg bg-white dark:bg-slate-800 overflow-hidden sm:border border-slate-200 dark:border-slate-700"
                >
                    <div
                        class="h-[248px] sm:h-[264px] w-full bg-slate-50 sm:bg-white dark:bg-slate-800 flex items-center justify-center"
                    >
                        <slot name="image">
                            <img
                                src="/images/tour-guide/graphics_tour_start.png"
                                alt="Welcome to My10x"
                                class="h-full object-contain"
                            />
                        </slot>
                    </div>
                    <div
                        class="flex flex-col items-start gap-3 w-full px-6 pt-8 pb-12 sm:px-8 sm:pt-8 sm:pb-6 bg-white dark:bg-slate-800"
                    >
                        <div
                            class="flex flex-col justify-center items-start gap-3 w-full"
                        >
                            <h3
                                class="text-2xl leading-8 sm:text-[20px] sm:leading-7 font-semibold sm:font-medium text-slate-800 dark:text-slate-100"
                            >
                                Welcome to My10x
                            </h3>
                            <p
                                class="text-[14px] leading-5 sm:text-[14px] sm:leading-5 text-slate-500 dark:text-slate-400 w-full"
                            >
                                We’re excited to have you on board. Take a quick
                                tour to discover key features and learn how to
                                make the most of your travel rewards.
                            </p>
                        </div>
                        <div
                            class="flex items-start gap-4 w-full pt-6 sm:pt-3 sm:flex sm:items-center sm:justify-end sm:gap-6"
                        >
                            <button
                                type="button"
                                class="inline-flex items-center justify-center gap-2 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-2.5 shadow-sm dark:shadow-slate-900/50 text-[14px] leading-5 font-medium text-slate-800 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-700 flex-1 sm:flex-none sm:w-auto sm:py-2 sm:rounded-md"
                                @click="openSkip"
                            >
                                Skip for now
                            </button>
                            <button
                                type="button"
                                class="inline-flex justify-center items-center gap-2 rounded bg-[#FD421D] px-4 py-2.5 shadow-sm text-[14px] leading-5 font-medium text-white hover:brightness-95 flex-1 sm:flex-none sm:w-auto sm:py-2 sm:rounded-md"
                                @click="onStart"
                            >
                                Start tour
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </transition>
    </Teleport>

    <!-- Skip guided tour modal -->
    <UiModal
        v-if="skipOpen"
        :key="skipOpen ? 'skip_open' : 'skip_closed'"
        :is-open="skipOpen"
        :is-teleported="true"
        title="Skip the tour?"
        width="520px"
        @close="closeSkip"
    >
        <template #content>
            <div>
                <p class="text-sm text-slate-800 dark:text-slate-100">
                    If you'd prefer, you can return to the My10x tour later from
                    the left-hand menu.
                </p>
            </div>
        </template>
        <template #footer>
            <div class="flex items-center justify-end gap-3">
                <button
                    type="button"
                    class="inline-flex items-center gap-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-2 text-sm font-medium text-slate-800 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-700"
                    @click="handleSkipForNow"
                >
                    Skip for now
                </button>
                <button
                    type="button"
                    class="inline-flex items-center gap-2 rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:brightness-95"
                    @click="handleContinueTour"
                >
                    Continue tour
                </button>
            </div>
        </template>
    </UiModal>
</template>

<script setup>
import { onMounted, ref, watch, computed, nextTick } from 'vue';
import { usePage } from '@inertiajs/vue3';
import UiModal from '@/components/ui/modal/ui-modal.vue';
import tourTracking from '@/utils/tourTracking.js';
import { Apii } from '@/services/api.js';

const props = defineProps({ enabled: { type: Boolean, default: true } });

const page = usePage();
const visible = ref(false);
const skipOpen = ref(false);
const dataSyncVisible = ref(false);
const modalOpen = ref(false);

const containerPositionClasses = computed(() => {
    const z = modalOpen.value ? 'z-10' : 'z-40';
    const bottomDesktop = dataSyncVisible.value
        ? 'sm:bottom-24'
        : 'sm:bottom-6';
    return [
        'fixed',
        z,
        'inset-0',
        'flex',
        'items-end',
        'justify-center',
        'sm:block',
        'sm:inset-auto',
        'sm:right-2',
        'sm:left-auto',
        'sm:top-auto',
        bottomDesktop,
    ].join(' ');
});

const isOnboarded = computed(() => {
    const p = page.props.value || {};
    const ud = p.userData || {};

    if (ud && typeof ud.tour_dismissed !== 'undefined') {
        return !!ud.tour_dismissed;
    }

    const au = p.auth?.user || {};
    if (au && typeof au.tour_dismissed !== 'undefined') {
        return !!au.tour_dismissed;
    }

    return false;
});

function canShow() {
    return !!props.enabled;
}
function onSkip() {
    visible.value = false;
}
function openSkip() {
    skipOpen.value = true;
}
function closeSkip() {
    skipOpen.value = false;
}

const ApiService = new Apii();

async function handleSkipForNow() {
    tourTracking.trackTeaserDismissed();
    closeSkip();
    visible.value = false;
    try {
        if (typeof window.hideTourTeaser === 'function')
            window.hideTourTeaser();
    } catch (_) {}
    try {
        window.dispatchEvent(new CustomEvent('show-tour-cta'));
    } catch (_) {}
    await nextTick();
    try {
        await ApiService.apiCall('/tour/skip-teaser', 'post');
    } catch (_) {}
    try {
        setTimeout(() => {
            try {
                document.querySelectorAll('[role="dialog"]').forEach((dlg) => {
                    try {
                        dlg.parentNode && dlg.parentNode.removeChild(dlg);
                    } catch (_) {}
                });
            } catch (_) {}
        }, 80);
    } catch (_) {}
}
function handleContinueTour() {
    closeSkip();
    visible.value = true;
    try {
        if (typeof window.showTourTeaser === 'function')
            window.showTourTeaser();
    } catch (_) {}
}

function onStart() {
    // Note: Tour started tracking is handled in TourManager.vue when the tour actually starts
    const tryStart = () => {
        try {
            if (typeof window.openSidebar === 'function') window.openSidebar();
        } catch (_) {}
        if (typeof window.launchUserTour === 'function') {
            try {
                window.__tourStartSource = 'teaser';
            } catch (_) {}
            window.launchUserTour();
            visible.value = false;
            return true;
        }
        return false;
    };
    if (!tryStart()) {
        const t = setInterval(() => {
            if (tryStart()) clearInterval(t);
        }, 250);
    }
}

onMounted(() => {
    visible.value = canShow();

    try {
        console.debug(
            '[TourTeaser] mounted, enabled=',
            props.enabled,
            'tour_dismissed=',
            isOnboarded.value
        );
    } catch (_) {}

    watch(
        isOnboarded,
        (tourDismissed) => {
            try {
                console.debug(
                    '[TourTeaser] tour dismissed changed ->',
                    tourDismissed
                );
            } catch (_) {}
            if (tourDismissed) visible.value = false;
        },
        { immediate: true }
    );

    window.addEventListener('user-tour-started', () => {
        visible.value = false;
    });

    const dataSyncShowHandler = () => {
        dataSyncVisible.value = true;
    };
    const dataSyncHideHandler = () => {
        dataSyncVisible.value = false;
    };
    window.addEventListener('data-sync-show', dataSyncShowHandler);
    window.addEventListener('data-sync-hide', dataSyncHideHandler);

    window.__debugTeaserDataSync = () => ({
        dataSyncVisible: dataSyncVisible.value,
    });

    const checkModals = () => {
        const modals = document.querySelectorAll('.modal');
        modalOpen.value = modals.length > 0;
    };
    const observer = new MutationObserver(checkModals);
    observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class'],
    });
    checkModals();

    window.showTourTeaser = () => {
        if (canShow()) visible.value = true;
    };
    window.hideTourTeaser = () => {
        visible.value = false;
    };
    window.showTourTeaserForce = () => {
        visible.value = true;
    };
});
watch(visible, (v) => {
    try {
        window.dispatchEvent(
            new CustomEvent(v ? 'tour-teaser-open' : 'tour-teaser-close')
        );

        // Track when teaser is shown
        if (v) {
            tourTracking.trackTeaserShown();
        }
    } catch (_) {}
});
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
